"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFuturesTPSLParams = void 0;
const decimal_js_1 = __importDefault(require("decimal.js"));
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_utils_lib_1 = require("trade_utils_lib");
const TriggersApi_1 = require("../../../../apis/TriggersApi");
const CustomTriggersProvider_1 = require("../../../../component/CustomTriggersProvider");
const interface_1 = require("../../../../interface");
const TPSLUtils_1 = require("./TPSLUtils");
function getPercent(position, triggerPrice, mode) {
    const { position_side, leverage, avgPrice } = position;
    return TPSLUtils_1.TPSLUtils.getTPSLPercent({ price: triggerPrice || "", side: position_side, leverage, avgPrice, mode });
}
function getTriggerPrice(position, percent, mode) {
    const { position_side, leverage, avgPrice } = position;
    return TPSLUtils_1.TPSLUtils.getTPSLPrice({ percent, side: position_side, leverage, avgPrice, mode });
}
function getEstPnl(position, triggerPrice, mode) {
    const { position_side, leverage, avgPrice, positionQuantity } = position;
    return TPSLUtils_1.TPSLUtils.getTPSLProfit({
        price: triggerPrice,
        side: position_side,
        leverage,
        avgPrice,
        positionQuantity,
        mode,
    });
}
/**设置仓位止盈止损 */
function useFuturesTPSLParams(props) {
    const { position, associateOrder, latestPrice, precision = 0, quoteDisplay, mode, accountType } = props;
    const { trigger_price, trigger_price_type, trigger_id } = associateOrder || {};
    const initWithOrder = (0, react_1.useRef)(false);
    /**百分比 */
    const [percent, setPercent] = (0, react_1.useState)(0);
    /**当前设置的价格 */
    const [triggerPrice, setTriggerPrice] = (0, react_1.useState)(trigger_price || "");
    const [priceType, setPriceType] = (0, react_1.useState)(trigger_price_type !== null && trigger_price_type !== void 0 ? trigger_price_type : interface_1.TriggerPriceType.LAST_PRICE);
    const { position_side, maint_price, symbol, position_id } = position;
    // 该hooks用于异步初始化
    (0, react_1.useEffect)(() => {
        if (initWithOrder.current || !associateOrder) {
            return;
        }
        initWithOrder.current = true;
        const percent = getPercent(position, associateOrder.trigger_price, mode);
        setPercent(percent);
        setTriggerPrice(associateOrder.trigger_price);
        setPriceType(associateOrder.trigger_price_type);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [associateOrder]);
    /**
     * buy reduce     short buy   平空
     *  sell  reduce      long sell    平多
     */
    const createOrder = (0, react_1.useCallback)(() => {
        return (0, TriggersApi_1.createTriggerOrder)({
            accountType,
            symbol,
            type: interface_1.CustomOrderType.CLOSE,
            side: position_side === interface_1.FuturePositionSide.LONG ? interface_1.CustomOrderSide.SELL : interface_1.CustomOrderSide.BUY,
            position_side,
            trigger_position_id: position_id,
            trigger_price: triggerPrice,
            trigger_price_type: priceType,
            trigger_type: TPSLUtils_1.TPSLUtils.getTriggerType(mode, position_side),
        });
    }, [accountType, mode, position_id, position_side, priceType, symbol, triggerPrice]);
    /**检测用户是否修改过价格 */
    const checkUserModified = (0, react_1.useCallback)(() => {
        return !new trade_utils_lib_1.SafeDecimal(trigger_price).eq(new trade_utils_lib_1.SafeDecimal(triggerPrice)) || priceType !== trigger_price_type;
    }, [priceType, triggerPrice, trigger_price, trigger_price_type]);
    const changePercent = (0, react_1.useCallback)((p) => {
        setPercent(p);
        // 重新计算价格
        const newPrice = getTriggerPrice(position, p, mode);
        setTriggerPrice(new trade_utils_lib_1.SafeDecimal(newPrice).toFixed(precision, decimal_js_1.default.ROUND_FLOOR));
    }, [mode, position, precision]);
    const changePrice = (0, react_1.useCallback)((price) => {
        setTriggerPrice(price);
        const newPercent = getPercent(position, price, mode);
        setPercent(new trade_utils_lib_1.SafeDecimal(newPercent).toDP(4, decimal_js_1.default.ROUND_FLOOR).toNumber());
    }, [mode, position]);
    // 计算预估盈亏
    const profitInfo = (0, react_1.useMemo)(() => {
        function getProfitValue() {
            const profit = getEstPnl(position, triggerPrice, mode);
            const isIncrease = profit.gte(0);
            return {
                profit: `${profit.toFixed(precision, decimal_js_1.default.ROUND_FLOOR)} ${quoteDisplay !== null && quoteDisplay !== void 0 ? quoteDisplay : "USDT"}`,
                increase: isIncrease,
                percent: new trade_utils_lib_1.SafeDecimal(percent)
                    .abs()
                    .mul(isIncrease ? 1 : -1)
                    .toNumber(),
            };
        }
        const errorInfo = TPSLUtils_1.TPSLUtils.checkTriggerPriceError(triggerPrice, latestPrice, position_side, mode, maint_price, percent);
        if (!errorInfo) {
            return {
                data: getProfitValue(),
            };
        }
        return {
            error: errorInfo,
        };
    }, [latestPrice, maint_price, mode, percent, position, position_side, precision, quoteDisplay, triggerPrice]);
    const validate = (0, react_1.useCallback)(() => {
        const error = profitInfo.error;
        const noop = {
            type: "IDLE",
            executor() {
                return (0, rxjs_1.of)(null);
            },
        };
        if (error && error.code === TPSLUtils_1.TPSLErrorCode.INVALID_PRICE) {
            if (trigger_id) {
                return (0, rxjs_1.of)({
                    type: "CANCEL_ORDER",
                    executor: () => (0, CustomTriggersProvider_1.cancelTriggerOrder)({
                        accountType,
                        symbol,
                        triggerId: trigger_id,
                    }),
                });
            }
            else {
                // 没有任何操作
                return (0, rxjs_1.of)(noop);
            }
        }
        if (!error) {
            if (!checkUserModified()) {
                // 未发生变跟
                return (0, rxjs_1.of)(noop);
            }
            if (trigger_id) {
                // 先取消订单，再重新下单
                return (0, rxjs_1.of)({
                    type: "UPDATE_ORDER",
                    executor: () => (0, CustomTriggersProvider_1.cancelTriggerOrder)({
                        accountType,
                        symbol,
                        triggerId: trigger_id,
                    }).pipe((0, operators_1.mergeMap)(createOrder)),
                });
            }
            // 直接下单
            return (0, rxjs_1.of)({
                type: "CREATE_ORDER",
                executor: createOrder,
            });
        }
        return (0, rxjs_1.throwError)(error);
    }, [accountType, checkUserModified, createOrder, profitInfo.error, symbol, trigger_id]);
    /**获取当前价格对应的百分比 */
    const latestPricePercent = (0, react_1.useMemo)(() => {
        return getPercent(position, latestPrice, mode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestPrice]);
    const resetParams = (0, react_1.useCallback)(() => {
        setPercent(0);
        setTriggerPrice("");
    }, []);
    return {
        changePercent,
        percent,
        changePrice,
        triggerPrice,
        estPnl: profitInfo,
        validate,
        latestPrice,
        estLiqPrice: maint_price,
        priceType,
        setPriceType,
        resetParams,
        latestPricePercent,
    };
}
exports.useFuturesTPSLParams = useFuturesTPSLParams;
